<template>
  <div class="results">
    <filter-panel :class="{ active: isMenuOpen }" @close="setMenu(false)" />
    <div class="results-options">
      <div class="sort-filter">
        <ag-button priority="text" @click="setMenu(true)">
          <ag-icon><filter-icon /></ag-icon>
           <span>Filter</span>
          </ag-button>
      </div>
      <div class="view-selector">
        <label>View:</label>
        <ag-button
          :disabled="view === $constant.viewType.GRID" 
          :class="{active: view === $constant.viewType.GRID}" 
          priority="secondary" 
          @click="onToggleView"
        >
          <ag-icon><grid-icon /></ag-icon>
        </ag-button>
        <ag-button 
          :disabled="view === $constant.viewType.LIST" 
          :class="{active: view === $constant.viewType.LIST}" 
          priority="secondary" 
          @click="onToggleView"
        >
          <ag-icon><list-icon /></ag-icon>
        </ag-button>
      </div>
    </div>
    <list-view
      v-if="view === $constant.viewType.LIST"
      :data="paginatedData"
      @sort="onSort"
      @view="onView"
    />
    <grid-view
      v-else
      :data="paginatedData"
      @view="onView"
    />
    <empty-list filtered class="empty-list" v-if="!count" entity-label="recipes">
      <book-open-icon />
    </empty-list>
    <pagination
      v-if="showPagination"
      :current-page="pageNum"
      :rows-per-page="this.pageSize"
      :total-rows="filteredRecordCount"
      @change-page="onChangePage"
      @previous-page="onPreviousPage"
      @next-page="onNextPage"
    />
  </div>
</template>

<script>
  import { get } from "lodash";
  import { mapState } from "vuex";
  import { vueWindowSizeMixin } from "vue-window-size";
  import { FilterIcon, ListIcon, GridIcon, BookOpenIcon } from "vue-feather-icons";
  import ListView from "./ListView";
  import GridView from "./GridView";
  import Pagination from "./Pagination";
  import FilterPanel from "../../views/Recipes/components/RecipesFilterPanel";
  import EmptyList from "../EmptyList";

  export default {
    name: "Results",
    mixins: [vueWindowSizeMixin],
    props: {
      data: {
        type: Array,
        required: true
      },
      mobileWidthBreakpoint: {
        type: Number,
        default: 992
      },
      actionLabel: {
        type: String,
        default: "Add New"
      }
    },
    data: () => ({
      isMenuOpen: false
    }),
    computed: {
      ...mapState("recipes", ["query", "view", "columns", "sort", "defaultSort", "count", "pageNum", "pageSize"]),
      filteredData() {
        if (!this.query) {
          return this.data;
        }

        const query = this.query.toLowerCase();
        const columns = this.columns;

        return this.data.filter(row => {
          for (let i = 0; i < columns.length; i++) {
            const value = row[columns[i].field];
            if (value) {
              if (
                value
                  .toString()
                  .toLowerCase()
                  .indexOf(query) >= 0
              ) {
                return true;
              }
            }
          }
          return false;
        });
      },
      sortedAndFilteredData() {
        // Filter first so we have less to compare for efficiency reasons
        const filteredData = this.filteredData;
        const sort = this.sort;

        if (!sort || !filteredData) {
          return filteredData;
        }

        const sortColumn = sort.column;
        const sortDirection = sort.direction;

        return filteredData.slice(0).sort((a, b) => {
          const aVal = get(a, sortColumn, null) || null;
          const bVal = get(b, sortColumn, null) || null;
          let compare;
          if(sortColumn === "prepTime" || sortColumn === "cookTime" || sortColumn === "servings") {
            compare = aVal > bVal ? 1 : -1;
          } else {
            compare = aVal.toString().localeCompare(bVal.toString());
          }
          return sortDirection === "ASC" ? compare : compare * -1;
        });
      },
      filteredRecordCount() {
        const count = get(this, "sortedAndFilteredData.length", 0);
        this.$store.dispatch(this.$action.RECIPES.SET_RECIPES_COUNT, count);
        return count;
      },
      paginatedData() {
        const data = this.sortedAndFilteredData;

        if (!data) {
          return data;
        }

        return data.slice(
          (this.pageNum - 1) * this.pageSize,
          this.pageNum * this.pageSize
        );
      },
      showPagination() {
        return (
          Math.ceil(this.filteredRecordCount / this.pageSize) > 1
        );
      }
    },
    methods: {
      onSort(column) {
        const currentSort = this.sort;

        // Currently no sort or sorted on a different column
        if (!currentSort || currentSort.column !== column.field) {
          const sortObj = {
            column: column.field,
            direction: "ASC"
          }
          this.$store.dispatch(this.$action.RECIPES.SET_SORT, sortObj);
          return;
        }

        const currentDirection = this.sort.direction;

        const sortObj = {
          column: column.field,
          direction: "DESC"
        }

        currentDirection === "ASC"
            ? this.$store.dispatch(this.$action.RECIPES.SET_SORT, sortObj)
            : this.$store.dispatch(this.$action.RECIPES.SET_SORT, this.defaultSort) || null;
      },
      onAdd() {
        this.$emit("add");
      },
      onView(row) {
        this.$emit("view", row);
      },
      onChangePage(page) {
        this.$store.dispatch(this.$action.RECIPES.SET_PAGE_NUM, page);
      },
      onPreviousPage() {
        this.$store.dispatch(this.$action.RECIPES.SET_PAGE_PREV);
      },
      onNextPage() {
        const nextPageNum = this.pageNum + 1;
        this.$store.dispatch(this.$action.RECIPES.SET_PAGE_NUM, nextPageNum);
      },
      onToggleView() {
        this.$store.dispatch(this.$action.RECIPES.SET_VIEW_TYPE, this.view === this.$constant.viewType.LIST ? this.$constant.viewType.GRID : this.$constant.viewType.LIST);
      },
      setMenu(isMenuOpen) {
        this.isMenuOpen = isMenuOpen;
      },
    },
    components: { Pagination, GridView, ListView, EmptyList, FilterIcon, ListIcon, GridIcon, BookOpenIcon, FilterPanel }
  };
</script>

<style scoped lang="scss">
  .results {
    display: flex;
    flex-direction: column;
    padding: $gutter;
    padding-bottom: 0;
    border-radius: $radius;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);

    .results-options {
      display: flex;
      margin-bottom: $gutter;
    }

    .sort-filter {
      flex-grow: 1;

      button {
        font-weight: $font-semibold;
        padding: 8px;
        margin-left: -8px;
        min-width: 0;
        color: $color-secondary;
        display: inline-flex;
        align-items: center;

        span {
          color: #999;
          margin-left: 6px;
          text-transform: uppercase;
        }

        &:hover {
          span {
            color: $color-secondary;
          }
        }
      }
    }

    .view-selector {
      display: flex;
      align-items: center;
      margin-right: -6px;

      label {
        text-transform: uppercase;
        font-weight: $font-semibold;
        color: #999;
      }

      button {
        min-width: 0;
        padding: 6px;
        margin-left: 2px;
        border: 0;
        box-shadow: none;
        color: #999;
        fill: #999;

        &:disabled {
          background: transparent;
        }

        &.active {
          color: $color-secondary;
          fill: $color-secondary;
        }
      }
    }

    .pagination {
      margin: 8px -20px 0 -20px;
    }
  }
</style>
